import React from 'react';
import LegalLayout from './LegalLayout';
import { AlertTriangle } from 'lucide-react';

const Article = ({ num, title, children }) => (
  <section className="border-l-4 border-[#205F79]/20 pl-5 py-1">
    <h2 className="text-xl font-bold text-gray-900 mb-3">
      <span className="text-[#205F79]">Article {num}</span> — {title}
    </h2>
    <div className="space-y-3 text-[15px] leading-relaxed">{children}</div>
  </section>
);

const CGU = () => (
  <LegalLayout title="Conditions Générales d'Utilisation et de Vente (CGU/CGV)" lastUpdated="Mai 2026">
    <p className="text-gray-600">
      Les présentes Conditions Générales d'Utilisation et de Vente (ci-après « CGU/CGV ») régissent l'accès et l'usage de
      la plateforme <strong>DIP PILOTE +IA</strong> (ci-après « le Service ») éditée par DIP PILOTE +IA (ci-après
      « l'Éditeur »). Toute utilisation du Service implique l'acceptation pleine et entière des présentes.
    </p>

    <Article num={1} title="Définitions">
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Service</strong> : la plateforme SaaS DIP PILOTE +IA accessible via abonnement.</li>
        <li><strong>Utilisateur</strong> : toute personne, physique ou morale, ayant créé un compte sur le Service.</li>
        <li><strong>Plan Gratuit</strong> : offre d'utilisation limitée, sans paiement.</li>
        <li><strong>Plan Premium</strong> : offre payante donnant accès aux fonctionnalités illimitées.</li>
        <li><strong>DIP</strong> : Dossier d'Information sur le Produit, document obligatoire en application du Règlement (CE) n° 1223/2009.</li>
        <li><strong>IA</strong> : Assistant d'intelligence artificielle intégré au Service.</li>
      </ul>
    </Article>

    <Article num={2} title="Objet">
      <p>
        Le Service a pour objet de fournir aux artisans cosmétiques et laboratoires de petite et moyenne taille un outil
        de gestion de production (matières premières, recettes, stocks, coûts de revient, prévisionnels de production)
        et d'aide à la rédaction du DIP conforme au Règlement (CE) n° 1223/2009 relatif aux produits cosmétiques.
      </p>
      <p>
        Le Service est strictement réservé à un usage <strong>professionnel</strong> ou semi-professionnel dans le cadre
        d'une activité de fabrication, formulation ou mise sur le marché de produits cosmétiques.
      </p>
    </Article>

    <Article num={3} title="Création de compte et accès au Service">
      <p>
        L'accès au Service nécessite la création d'un compte utilisateur via une adresse email valide et un mot de passe.
        L'Utilisateur garantit l'exactitude des informations communiquées et s'engage à les tenir à jour.
      </p>
      <p>
        L'Utilisateur est seul responsable de la confidentialité de ses identifiants et de toute action effectuée depuis
        son compte. En cas d'usage frauduleux suspecté, il s'engage à en informer l'Éditeur sans délai.
      </p>
    </Article>

    <Article num={4} title="Modèle commercial — Freemium">
      <p>Le Service est proposé selon un modèle freemium en deux formules :</p>

      <div className="grid sm:grid-cols-2 gap-4 my-4">
        <div className="border border-gray-200 rounded-lg p-5 bg-white">
          <p className="text-xs uppercase tracking-wider font-semibold text-gray-500 mb-2">Plan Gratuit</p>
          <p className="text-2xl font-bold text-gray-900 mb-3">0 €</p>
          <ul className="text-sm space-y-1 text-gray-700 list-disc pl-4">
            <li>Jusqu'à <strong>3 produits finis</strong> simultanés</li>
            <li>Jusqu'à <strong>10 matières premières</strong> simultanées</li>
            <li>Accès à l'assistant IA (usage standard)</li>
            <li>Module Production limité (pas de prévisionnel)</li>
          </ul>
        </div>
        <div className="border-2 border-[#205F79] rounded-lg p-5 bg-[#205F79]/5">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#205F79] mb-2">Plan Premium</p>
          <p className="text-2xl font-bold text-gray-900 mb-1">25 € / mois <span className="text-sm font-normal text-gray-500">ou 250 € / an</span></p>
          <p className="text-xs text-gray-500 mb-3">TTC, paiement sécurisé Stripe</p>
          <ul className="text-sm space-y-1 text-gray-700 list-disc pl-4">
            <li>Produits et matières <strong>illimités</strong></li>
            <li>Génération de DIP PDF complets</li>
            <li>Module Production complet (prévisionnel, planning)</li>
            <li>Assistant IA avec actions automatisées</li>
            <li>Support prioritaire</li>
          </ul>
        </div>
      </div>

      <p>
        Le passage du Plan Gratuit au Plan Premium se fait à tout moment depuis la page « Abonnement ». Les paiements
        sont gérés par <strong>Stripe Inc.</strong>, prestataire certifié PCI-DSS niveau 1. L'Éditeur n'a accès à aucune
        donnée bancaire.
      </p>
      <p>
        En cas de retour au Plan Gratuit (annulation ou expiration), les données excédentaires (produits ou matières
        au-delà des limites) sont <strong>verrouillées en lecture seule</strong> pendant une période de grâce de 30 jours
        avant désactivation, mais ne sont jamais supprimées tant que le compte existe.
      </p>
    </Article>

    <Article num={5} title="Sans engagement et résiliation">
      <p>
        L'abonnement Premium <strong>mensuel</strong> est sans engagement. L'Utilisateur peut le résilier à tout moment
        depuis la page « Abonnement ». La résiliation prend effet à la fin de la période en cours déjà payée
        (30 jours après le dernier paiement). Aucun remboursement au prorata n'est effectué.
      </p>
      <p>
        L'abonnement Premium <strong>annuel</strong> (250 €/an) est une souscription unique non remboursable. Il prend
        automatiquement fin à l'échéance des 12 mois et peut être renouvelé manuellement.
      </p>
      <p>
        L'Utilisateur peut à tout moment supprimer son compte. Cette action entraîne la suppression définitive de toutes
        ses données dans un délai maximal de 30 jours, conformément à la politique RGPD.
      </p>
    </Article>

    <Article num={6} title="ASSISTANT INTELLIGENCE ARTIFICIELLE — Limitation de responsabilité (Clause cruciale)">
      <div className="border-l-4 border-amber-500 bg-amber-50 p-5 rounded-r-lg my-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
          <div className="space-y-3">
            <p className="font-semibold text-amber-900">
              L'assistant IA intégré au Service est un OUTIL D'ASSISTANCE et d'AIDE À LA RÉDACTION. Il ne remplace en
              aucun cas l'expertise d'un toxicologue, d'une personne responsable au sens du Règlement (CE) n° 1223/2009,
              ni d'un évaluateur de la sécurité.
            </p>
            <p className="text-amber-800">
              L'Utilisateur reconnaît et accepte expressément que :
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-amber-800">
              <li>
                Les informations générées par l'IA (nomenclatures INCI, suggestions de dosages, calculs réglementaires,
                fonctions d'ingrédients, alertes substances) peuvent contenir des <strong>erreurs, omissions ou
                approximations</strong>.
              </li>
              <li>
                Il est <strong>seul responsable</strong> de la <strong>vérification, validation et conformité finale</strong>
                de toute donnée saisie ou générée, notamment :
                <ul className="list-disc pl-5 mt-1">
                  <li>Des nomenclatures INCI utilisées,</li>
                  <li>Des dosages et pourcentages déclarés,</li>
                  <li>Du respect des annexes du Règlement (CE) n° 1223/2009 (substances interdites, restreintes, conservateurs, filtres UV…),</li>
                  <li>De la cohérence du DIP avant toute mise sur le marché.</li>
                </ul>
              </li>
              <li>
                Le DIP généré par le Service est un <strong>support documentaire structuré</strong>, dont la conformité
                définitive et l'évaluation de la sécurité doivent être validées par une <strong>personne qualifiée</strong>
                au sens du Règlement.
              </li>
              <li>
                L'Éditeur <strong>décline toute responsabilité</strong> en cas de dommage direct ou indirect résultant
                d'une erreur de l'IA, d'une donnée erronée fournie par l'IA, d'une mauvaise interprétation par
                l'Utilisateur, ou de la mise sur le marché d'un produit non conforme.
              </li>
              <li>
                L'usage du Service ne dispense pas l'Utilisateur de ses obligations de notification CPNP, de désignation
                d'une personne responsable, de tenue des bonnes pratiques de fabrication (ISO 22716) et de conservation
                du DIP pendant 10 ans après mise sur le marché du dernier lot.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Article>

    <Article num={7} title="Obligations de l'Utilisateur">
      <p>L'Utilisateur s'engage à :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Ne pas utiliser le Service à des fins illicites, frauduleuses ou contraires à l'ordre public ;</li>
        <li>Respecter la réglementation applicable à son activité, notamment le Règlement (CE) n° 1223/2009 ;</li>
        <li>Ne pas tenter de pirater, désassembler, contourner les sécurités du Service ;</li>
        <li>Ne pas surcharger artificiellement le Service via scripts automatisés ou requêtes massives ;</li>
        <li>Ne pas utiliser l'IA pour générer des contenus contraires à la loi, diffamatoires ou contrefaisants.</li>
      </ul>
      <p>
        Tout manquement pourra entraîner la suspension immédiate du compte sans préavis et sans remboursement.
      </p>
    </Article>

    <Article num={8} title="Disponibilité du Service">
      <p>
        L'Éditeur s'efforce d'assurer une disponibilité du Service 24h/24 et 7j/7. Toutefois, des interruptions peuvent
        survenir pour des raisons de maintenance, de mises à jour, de défaillance des prestataires d'hébergement ou
        d'événements indépendants de sa volonté (force majeure, attaque informatique, panne réseau).
      </p>
      <p>
        L'Éditeur ne saurait être tenu responsable des préjudices résultant d'une indisponibilité temporaire du Service.
      </p>
    </Article>

    <Article num={9} title="Limitation de responsabilité">
      <p>
        L'Éditeur ne saurait être tenu responsable :
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Des conséquences directes ou indirectes liées à l'utilisation du Service ou de l'IA par l'Utilisateur ;</li>
        <li>De la perte de données causée par un usage incorrect du Service ou un sinistre informatique ;</li>
        <li>D'une non-conformité réglementaire d'un produit cosmétique mis sur le marché par l'Utilisateur ;</li>
        <li>De tout préjudice commercial, financier, d'image ou de réputation lié à l'usage du Service.</li>
      </ul>
      <p>
        En tout état de cause, et sauf dispositions légales impératives contraires, la responsabilité de l'Éditeur est
        limitée au montant total des sommes effectivement versées par l'Utilisateur au cours des 12 derniers mois
        précédant le fait générateur.
      </p>
    </Article>

    <Article num={10} title="Données personnelles">
      <p>
        Le traitement des données personnelles est détaillé dans la <a href="/legal/confidentialite" className="text-[#205F79] underline hover:no-underline">Politique de Confidentialité (RGPD)</a>,
        partie intégrante des présentes CGU/CGV.
      </p>
    </Article>

    <Article num={11} title="Modification des CGU/CGV">
      <p>
        L'Éditeur se réserve le droit de modifier les présentes CGU/CGV à tout moment. Les Utilisateurs seront informés
        des modifications substantielles par email et / ou notification au sein du Service au moins 15 jours avant leur
        entrée en vigueur. La poursuite de l'utilisation du Service après cette date vaut acceptation des nouvelles CGU/CGV.
      </p>
    </Article>

    <Article num={12} title="Droit applicable et juridiction compétente">
      <p>
        Les présentes CGU/CGV sont régies par le droit français. Tout litige relatif à leur interprétation ou exécution
        sera, à défaut de résolution amiable, soumis aux tribunaux compétents du ressort du siège de l'Éditeur, sauf
        dispositions impératives contraires (notamment en matière de consommation).
      </p>
    </Article>

    <Article num={13} title="Médiation de la consommation">
      <p>
        Conformément à l'article L.612-1 du Code de la consommation, l'Utilisateur consommateur peut recourir gratuitement
        au service de médiation MEDICYS, en cas de litige n'ayant pu être résolu à l'amiable directement avec l'Éditeur.
        Les coordonnées du médiateur seront communiquées sur simple demande à <strong>dippilote@outlook.com</strong>.
      </p>
    </Article>
  </LegalLayout>
);

export default CGU;
